/* eslint-disable */
import { createContext, Dispatch, useContext } from 'react';

import { ViewState, ReducerAction } from 'social/publisher/Publisher.types';

export const PublisherContext = createContext<ViewState | undefined>(undefined);
export const PublisherSourcesContext = createContext<ViewState | undefined>(undefined);

export const PublisherDispatchContext = createContext<Dispatch<ReducerAction> | undefined>(undefined);

export const usePublisherDispatch = (): Dispatch<ReducerAction> => {
    const context = useContext(PublisherDispatchContext);

    if (context === undefined) {
        throw new Error('usePublisherDispatch must be used within a PublisherProvider');
    }

    return context;
};

export const usePublisherState = (): ViewState => {
    const context = useContext(PublisherContext);

    if (context === undefined) {
        throw new Error('usePublisherState must be used within a PublisherProvider');
    }

    return context;
};

export const usePublisher = (): [ViewState, Dispatch<ReducerAction>] => {
    return [usePublisherState(), usePublisherDispatch()];
};

export const initialViewState: ViewState = {
    profile: undefined, // User profile
    postId: undefined,
    contentId: undefined,
    campaignId: null,
    campaignType: null,
    editorMode: 'POST_PUBLISH', // Editor mode
    selectedLocations: [], // LocationUids the user has selected
    selectedSources: {
        // Sources the user has selected
        FACEBOOK: false,
        TWITTER: false,
        INSTAGRAM: false,
        GOOGLE_PLACES: false,
        LINKEDIN: false,
        YOUTUBE: false,
        IG_STORIES: false,
        IG_REELS: false,
        TIKTOK: false,
    },
    enabledSources: {
        FACEBOOK: true,
        TWITTER: true,
        INSTAGRAM: true,
        GOOGLE_PLACES: true,
        LINKEDIN: true,
        YOUTUBE: true,
        IG_STORIES: true,
        IG_REELS: true,
        TIKTOK: true,
    },
    postType: 'MEDIA', // Whether a post is a 'media' or 'link' type
    title: '', // Content library item title or YouTube video title
    content: [], // Content MetaData
    scheduleDateTime: [], // Datetime for scheduled later
    media: [], // Media files
    mediaValidation: [], // Media files' matching validations
    locationSources: [], // Cached locations and sources the user has access to
    googleFields: { callToAction: null, alertType: null, event: null }, // Container object for Google Business Profile special values
    youtubeFields: { kidsVideo: false, embedVideo: true, publishToFeed: true }, // Container object for Youtube special values
    publisherErrors: [], // Validation result and its errors
    publishType: 'PUBLISH_NOW', // How the publisher will behave on submit
    enabledPublishTypes: ['PUBLISH_NOW', 'PUBLISH_LATER', 'OPTIMAL_TIMES', 'DRAFT'],
    schedulePostResponse: undefined, // Cached response after submit
    urlMetaData: null, // Cached url metadata for link post
    isShowPostSummary: false, // If the Post Summary modal is showing
    isSingleLocationUser: false, // If the user only has access to one location
    isSingleSourceUser: false, // If the user only has access to one source
    hasSocialPermission: false, // If the user has permission to add or edit posts
    hasApproveAutoPermission: false, // If the user has the permission to auto-approve their post
    hasProfilePublishPermission: false, // If the user has the directory profiles publishing perimssion
    hasTagAddPermission: false,
    hasLabelAddPermission: false,
    hasLabelViewPermission: false,
    hasContentAddGlobalPermission: false, // If the user has the permission to add content for all locations
    hasTargetAudiencePermission: false,
    hasUserSelectedLocationsManually: false, // If the user has manually selected locations (versus locations picked automatically by source selector)
    hasUserChosenMediaPostType: false, // Flag to ensure we don't rerun url checks if a user has opted for a link to be a media type
    isSourcesLocked: false, //
    isLocationsLocked: false, //
    isLocationsDisabled: false, //
    isAssetsLocked: false, //
    isAssetsDisabled: false, //
    isTitleLocked: false, //
    isTitleDisabled: true, //
    isContentLocked: false, //
    isTagsLocked: false, //
    isTagsDisabled: true, //
    isLabelsDisabled: false, //
    isMediaLocked: false, //
    isPublishTimeLocked: false, //
    isPublishOptionsLocked: false, //
    isContentAndMediaLocked: false, //
    isShortenedUrl: false,
    promisesInTransit: [], // Active promises not resolved will be added here to keep track
    returnUrl: '/', // URL to return to once the publisher is closed/complete
    selectedTags: [],
    targetAudience: { FACEBOOK: {} },
    duplicatePost: false,
    locationTag: null,
    selectedLabels: [],
    isDMLinkAdded: false,
    customizeSourceKey: 'ALL_SOURCES',
    isCustomizingBySource: false,
    sourceStates: {
        ALL_SOURCES: null,
        FACEBOOK: null,
        INSTAGRAM: null,
        TWITTER: null,
        GOOGLE_PLACES: null,
        LINKEDIN: null,
        YOUTUBE: null,
        IG_STORIES: null,
        IG_REELS: null,
        TIKTOK: null,
    },
};
